'use client';

/**
 * Workspace Selector Component
 * Allows users to switch between workspaces
 */

import { useState } from 'react';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';

export function WorkspaceSelector() {
  const { currentWorkspace, workspaces, switchWorkspace, loading } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-background/50 rounded-lg">
        <div className="w-4 h-4 border-2 border-border/30 border-t-cream rounded-full animate-spin" />
        <span className="text-sm text-foreground-secondary">Loading...</span>
      </div>
    );
  }

  if (!currentWorkspace) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-background/50 rounded-lg border border-red-500/50">
        <span className="text-sm text-red-400">No workspace</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-background/50 hover:bg-background/70 rounded-lg transition-colors border border-border"
      >
        <div className="flex items-center gap-2">
          {currentWorkspace.type === 'local' ? (
            <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            </svg>
          )}
          <span className="text-sm font-medium text-foreground">{currentWorkspace.name}</span>
        </div>
        <svg
          className={`w-4 h-4 text-foreground-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-background rounded-lg shadow-xl border border-border z-50">
            <div className="p-2 border-b border-border-muted">
              <p className="text-xs text-foreground-secondary px-2">Switch Workspace</p>
            </div>

            <div className="max-h-96 overflow-y-auto p-2">
              {workspaces.map(workspace => (
                <button
                  key={workspace.id}
                  onClick={() => {
                    switchWorkspace(workspace.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-start gap-3 px-3 py-2 rounded-lg transition-colors ${
                    workspace.id === currentWorkspace.id
                      ? 'bg-accent-muted border border-accent/40'
                      : 'hover:bg-background/70 border border-transparent'
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {workspace.type === 'local' ? (
                      <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                      </svg>
                    )}
                  </div>

                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{workspace.name}</p>
                      {workspace.isDefault && (
                        <span className="text-xs px-1.5 py-0.5 bg-accent-muted text-accent rounded">
                          Default
                        </span>
                      )}
                    </div>
                    {workspace.description && (
                      <p className="text-xs text-foreground-tertiary mt-0.5">{workspace.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-xs text-foreground-tertiary">
                      <span>{workspace.diagramCount} diagrams</span>
                      <span>{workspace.folderCount} folders</span>
                    </div>
                  </div>

                  {workspace.id === currentWorkspace.id && (
                    <svg className="w-5 h-5 text-accent flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
